import * as THREE from "three";
import { HqMaterials } from "../../hqMaterials";
import { SkinMaterials } from "../skinMaterial";
import { PATTERN_KINDS, SLOT_NAMES, type SlotLook, type TowerKit } from "./types";
import { SLOT_COUNT, SLOT_INDEX } from "./slots";
import { KIT_NOISE_GLSL } from "./glsl";
import { KitClock } from "./clock";
import { KIT_HIDE_GLSL, KitHideDepth } from "./hideMaterial";
import { hideMask } from "./hide";

/**
 * A tower kit's material set on the stock tower models: per material SLOT (kit/slots.ts — the part each vertex
 * belongs to) its own albedo grade, gain, metalness, roughness, emissive trim and a procedural surface pattern
 * (veins, grain, hammered metal, flecks, riveted plates). The look is uniforms in small per-slot tables; every kit
 * shares one program per source program (cache key "-kit"), and every tower of a type wears the same variant, so a
 * skinned tower draws exactly as many times as a stock one.
 *
 * Built on the same hooks as SkinMaterials (a copy of the converted material that keeps wind, haze and the lit
 * extras, then the kit's chunks): the palette swap `mix(albedo, tint × luma / RefLuma, mix) × gain` keeps the
 * painted light and dark, strongly coloured texels (a tower's painted accents) keep their paint unless the slot says
 * otherwise, and the element slot (crystals, coils, flames, brew, orb, rune) is never touched.
 */
export class KitBodyMaterials {
  private static readonly variants = new Map<THREE.Material, Map<string, THREE.MeshStandardMaterial>>();

  /** The packed per-slot tables of a kit (exported for the tests: the element slot is the identity). */
  static tables(kit: TowerKit): { tint: THREE.Vector4[]; pbr: THREE.Vector4[]; trim: THREE.Vector4[]; pat: THREE.Vector4[]; patColour: THREE.Vector4[] } {
    const t = { tint: [] as THREE.Vector4[], pbr: [] as THREE.Vector4[], trim: [] as THREE.Vector4[], pat: [] as THREE.Vector4[], patColour: [] as THREE.Vector4[] };
    for (let i = 0; i < SLOT_COUNT; i++) {
      const name = SLOT_NAMES.find((n) => SLOT_INDEX[n] === i);
      const s: SlotLook = (name ? kit.slots[name] : undefined) ?? {};
      const tint = new THREE.Color(s.tint ?? 0xffffff);
      t.tint.push(new THREE.Vector4(tint.r, tint.g, tint.b, s.tint === undefined ? 0 : s.mix ?? 0.6));
      t.pbr.push(new THREE.Vector4(s.gain ?? 1, s.metalness ?? -1, s.roughness ?? -1, s.keepAccents === false ? 0 : 1));
      const trim = s.trim ? new THREE.Color(s.trim.colour).multiplyScalar(s.trim.strength) : new THREE.Color(0, 0, 0);
      t.trim.push(new THREE.Vector4(trim.r, trim.g, trim.b, 0));
      const p = s.pattern;
      const pc = new THREE.Color(p?.colour ?? 0xffffff);
      t.pat.push(new THREE.Vector4(p ? PATTERN_KINDS.indexOf(p.kind) : 0, p?.scale ?? 1, p?.strength ?? 0, p?.glow ?? 0));
      t.patColour.push(new THREE.Vector4(pc.r, pc.g, pc.b, 0));
    }
    return t;
  }

  /**
   * The kit's variant of a converted lit material (one per source, kit and hide mask: `hide` is the kit's mask of
   * hidden stock parts, kit/hide.ts, where it may hide them; 0 hides nothing, and is what a kit without `hide` gets).
   */
  static variant(source: THREE.MeshStandardMaterial, kit: TowerKit, hide = 0): THREE.MeshStandardMaterial {
    let byKit = KitBodyMaterials.variants.get(source);
    if (!byKit) { byKit = new Map(); KitBodyMaterials.variants.set(source, byKit); }
    const key = hide ? `${kit.id}|h${hide}` : kit.id;
    let v = byKit.get(key);
    if (v) return v;
    v = source.clone();
    v.name = `${source.name}~${key}`;
    const t = KitBodyMaterials.tables(kit);
    const u = {
      uKT: { value: t.tint }, uKP: { value: t.pbr }, uKE: { value: t.trim }, uKN: { value: t.pat }, uKC: { value: t.patColour },
      uKitTime: KitClock.time, uKitMotion: KitClock.motion, uKHide: { value: hide },
    };
    const N = SLOT_COUNT, R = SkinMaterials.RefLuma.toFixed(3);
    const prev = source.onBeforeCompile;
    v.onBeforeCompile = (sh, r) => {
      prev.call(source, sh, r);
      Object.assign(sh.uniforms, u);
      sh.vertexShader = sh.vertexShader
        .replace("#include <common>", `#include <common>\nattribute float tdSlot;\nvarying float vTdSlot;\nvarying vec3 vTdPos;\n${KIT_HIDE_GLSL.head}`)
        .replace("#include <skinning_vertex>", "#include <skinning_vertex>\n  vTdSlot = tdSlot;\n  vTdPos = transformed;")
        .replace("#include <project_vertex>", `#include <project_vertex>${KIT_HIDE_GLSL.body}`);
      sh.fragmentShader = sh.fragmentShader
        .replace("#include <common>", `#include <common>
uniform vec4 uKT[${N}], uKP[${N}], uKE[${N}], uKN[${N}], uKC[${N}];
uniform float uKitTime, uKitMotion;
varying float vTdSlot;
varying vec3 vTdPos;
float tdL, tdLine, tdGlow, tdRough;
int tdS;
${KIT_NOISE_GLSL}`)
        .replace("#include <map_fragment>", `#include <map_fragment>
  {
    tdS = int(vTdSlot + 0.5);
    vec4 kt = uKT[tdS], kp = uKP[tdS], kn = uKN[tdS];
    vec3 c0 = diffuseColor.rgb;
    tdL = dot(c0, vec3(0.2126, 0.7152, 0.0722)) / ${R};
    float sat = (max(max(c0.r, c0.g), c0.b) - min(min(c0.r, c0.g), c0.b)) / max(max(max(c0.r, c0.g), c0.b), 1e-4);
    float keep = smoothstep(${SkinMaterials.KeepSatFrom.toFixed(3)}, ${SkinMaterials.KeepSatTo.toFixed(3)}, sat) * kp.w;
    vec3 c = mix(c0, kt.rgb * tdL, kt.a * (1.0 - keep)) * kp.x;
    // the slot's pattern, in the model's own space (stable on a tower, the same on every copy of it)
    tdLine = 0.0; tdGlow = 0.0; tdRough = 0.0;
    int kind = int(kn.x + 0.5);
    vec3 p = vTdPos * kn.y;
    if (kind == 1) {                                    // veins: marble, obsidian seams
      float n = tdNoise3(p * 0.5) * 2.0 + tdNoise3(p * 1.7) * 0.7;
      float v = abs(sin(dot(p, vec3(0.8, 0.45, 0.6)) + n * 3.0));
      tdLine = 1.0 - smoothstep(0.0, 0.14, v);
      tdGlow = tdLine * (0.75 + 0.25 * sin(uKitTime * uKitMotion * 1.3 + n * 4.0));
    } else if (kind == 2) {                             // grain: timber rings running up the tower
      float g = sin((length(vTdPos.xz) * 2.0 + vTdPos.y * 0.2) * kn.y + tdNoise3(p * vec3(1.5, 0.15, 1.5)) * 4.0);
      tdLine = smoothstep(0.2, 1.0, g);
    } else if (kind == 3) {                             // hammered: dimpled beaten metal
      vec3 f = fract(p) - 0.5;
      float h = tdHash3(floor(p));
      tdLine = (0.25 - dot(f, f)) * 2.0 * (0.5 + h);
      tdRough = tdLine * 0.25;
    } else if (kind == 4) {                             // flecks: glinting grains that twinkle
      float h = tdHash3(floor(p));
      tdLine = step(0.93, h);
      tdGlow = tdLine * (0.5 + 0.5 * sin(uKitTime * uKitMotion * 2.5 + h * 40.0));
    } else if (kind == 5) {                             // plates: riveted panels round the drum
      vec2 q = vec2(atan(vTdPos.x, vTdPos.z) * 1.2732, vTdPos.y) * kn.y;
      vec2 g2 = abs(fract(q) - 0.5);
      tdLine = 1.0 - smoothstep(0.40, 0.47, max(g2.x, g2.y));
      float sm = max(g2.x, g2.y);                       // a glow along the seams (forgeguard's hot seams): a hot core and a
      tdGlow = smoothstep(0.43, 0.49, sm) + 0.3 * smoothstep(0.37, 0.46, sm); // softer halo, wide enough to read at phone size
      vec2 rv = fract(q) - vec2(0.12, 0.5);
      tdLine = max(tdLine, 1.0 - smoothstep(0.03, 0.06, length(rv * vec2(1.0, 0.4))));
    }
    // painted accents keep their paint under the pattern too (an archer's green roof stays green, with gold trim)
    c = mix(c, uKC[tdS].rgb * max(tdL, 0.35), clamp(tdLine * kn.z, 0.0, 1.0) * (1.0 - keep));
    tdGlow *= 1.0 - keep;
    diffuseColor.rgb = c;
  }`)
        .replace("#include <roughnessmap_fragment>", `#include <roughnessmap_fragment>
  {
    vec4 kp = uKP[tdS];
    // the slot's roughness keeps a third of the painted map's variation, so a gilded roof still reads hammered
    if (kp.z >= 0.0) roughnessFactor = clamp(kp.z * mix(1.0, roughnessFactor / max(roughness, 1e-3), 0.35) + tdRough, 0.04, 1.0);
  }`)
        .replace("#include <metalnessmap_fragment>", `#include <metalnessmap_fragment>
  if (uKP[tdS].y >= 0.0) metalnessFactor = uKP[tdS].y;`)
        .replace("#include <emissivemap_fragment>", `#include <emissivemap_fragment>
  totalEmissiveRadiance += uKE[tdS].rgb * smoothstep(${SkinMaterials.TrimFrom.toFixed(3)}, ${SkinMaterials.TrimTo.toFixed(3)}, tdL);
  totalEmissiveRadiance += uKC[tdS].rgb * uKN[tdS].w * tdGlow;`);
    };
    const key0 = source.customProgramCacheKey();
    v.customProgramCacheKey = () => `${key0}-kit`;
    const entry = HqMaterials.standard.find((e) => e.mat === source);
    HqMaterials.standard.push({ mat: v, ground: entry?.ground ?? false });
    byKit.set(key, v);
    return v;
  }

  /** Whether a mesh takes the kit's body: the lit atlas (not the glow, the pennant team cloth, the pool). */
  static skinnable(m: THREE.Material): m is THREE.MeshStandardMaterial {
    return SkinMaterials.skinnable(m) && !/_glow$/.test(m.name ?? "") && !(m as THREE.MeshStandardMaterial).transparent;
  }

  /**
   * Dresses every lit mesh under `root` in the kit's body (or back in its stock material for null). The stock
   * material is remembered on the mesh, shared with SkinMaterials.dress, so the two never stack. `hide`: the mask of
   * stock parts hidden (default: all the kit asks for; the runtime passes 0 where the kit does not mount).
   */
  static dress(root: THREE.Object3D, kit: TowerKit | null, hide = hideMask(kit?.hide)): void {
    KitHideDepth.dress(root, kit ? hide : 0);
    root.traverse((o) => {
      const mesh = o as THREE.Mesh;
      if (!mesh.isMesh || Array.isArray(mesh.material) || o.userData.tdKit === true) return;
      const stock = (mesh.userData.tdSkinStock as THREE.Material | undefined) ?? mesh.material;
      if (!KitBodyMaterials.skinnable(stock)) return;
      if (!kit) {
        if (mesh.userData.tdSkinStock) { mesh.material = stock; delete mesh.userData.tdSkinStock; }
        return;
      }
      mesh.userData.tdSkinStock = stock;
      mesh.material = KitBodyMaterials.variant(stock, kit, hide);
    });
  }

  /** The library is unloading `mats`: their variants go too (HqMaterials.forget). */
  static forget(mats: ReadonlySet<THREE.Material>): THREE.Material[] {
    const gone: THREE.Material[] = [];
    for (const m of mats) {
      const byKit = KitBodyMaterials.variants.get(m);
      if (!byKit) continue;
      for (const v of byKit.values()) gone.push(v);
      KitBodyMaterials.variants.delete(m);
    }
    return gone;
  }
}
HqMaterials.forgetHooks.push((mats) => KitBodyMaterials.forget(mats));
